import Signal from "badmfck-signal";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../badge/Badge";
import { Box, HBox, VBox } from "../box/Box";
import Button from "../button/Button";
import { Checkbox } from "../checkbox/Checkbox";
import { Error } from "../error/Error";
import { IconArrowDown } from "../icons/IconArrowDown";
import { IconArrowRight } from "../icons/IconArrowRight";
import { IconVDots } from "../icons/IconVDots";
import Input from "../input/Input";
import Spacer from "../spacer/Spacer";
import { ProgressBar } from "../spinner/ProgressBar";
import { Typo, TypoMedium } from "../text/Typo";
import { Tile } from "../tile/Tile";
import { ICellFactoryResult, S_TABLE_EXPAND, Table } from "./Table";
import { TitledValue } from "./TitledValue";

type TOrder<T> = keyof T | "__itemType" | "__actions";

interface IRowsFilters<T> {
	name: string;
	field: keyof T;
	mask: string | RegExp | null;
}
interface IRowsProps<T> {
	data: T[];
	order?: TOrder<T>[];
	gridTemplateColumns?: string;
	itemType?: string;
	inlineItems?: boolean;
	onClick?: (row: T, cell?: any) => void;
	onExpand?: (row: T, rowID?: string) => JSX.Element | null;
	cellFactory?: (
		row: T,
		cell: any,
		index: number,
		key?: number,
		name?: string,
		sx?: React.CSSProperties,
		expanded?: boolean,
		rowID?: string,
	) => ICellFactoryResult;
	menu?: {
		title: string | ((row: T, rowID: string) => string | null);
		id: string | ((row: T, rowID: string) => string | null);
	}[];
	onMenuClick?: (id: string, row: T, rowID: string) => void;
	internal?: boolean;
	ignoreClick?: boolean;
	busy?: boolean;
	error?: any;
	searchable?: boolean;
	bulk?: boolean;
	filters?: IRowsFilters<T>[];
	scrollable?: boolean;
	controlsPadding?: string;
}

const resolveRowMenuItems = (menu: IRowsProps<any>["menu"], row: any, rowID: string) =>
	(menu ?? []).flatMap(item => {
		const title = typeof item.title === "function" ? item.title(row, rowID) : item.title;
		const id = typeof item.id === "function" ? item.id(row, rowID) : item.id;
		return title && id ? [{ title, id }] : [];
	});

const S_MENU_SHOW = new Signal<{
	rowsID: string;
	data: any;
}>();

const S_SEARCH = new Signal<{
	rowsID: string;
	data: any;
}>();

const S_MENU_FILTER = new Signal<{
	rowsID: string;
	data: IRowsFilters<any> | null;
}>();

export const Rows = <T,>({
	data,
	order,
	gridTemplateColumns,
	onClick,
	cellFactory,
	menu,
	onMenuClick,
	itemType,
	internal,
	onExpand,
	inlineItems,
	ignoreClick,
	busy,
	error,
	searchable,
	filters,
	bulk,
	controlsPadding,
}: IRowsProps<T>) => {
	const [width, setSize] = useState<number>(-1);

	const ref = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		if (!ref.current) return;
		const observer = new ResizeObserver(([entry]) => {
			setSize(entry.contentRect.width);
		});
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	const rowsID = useMemo(() => Math.random() + "" + +new Date() + Math.random() * Math.random(), []);

	if (error) {
		return <Error error={error} />;
	}

	if (busy !== undefined) {
		if (busy) {
			return (
				<Table
					sx={{
						rowGap: "4px",
						marginLeft: internal ? "30px" : undefined,
					}}
					ignoreClick={ignoreClick}
					headless
					data={[{ status: "Loading..." }]}
					cellFactory={(_row, cell, _index: number, _key, name, _expanded, _rowID) => {
						return {
							content: (
								<VBox gap={"10px"}>
									<TitledValue title={name ?? "--"} value={cell ?? "--"} />
									<ProgressBar busy={true} />
								</VBox>
							),
							sx: {
								borderRadius: "6px",
							},
						};
					}}
				/>
			);
		}
	}

	if (!data || !data.length) return null;

	if (menu === undefined) {
		menu = [
			{
				title: "View",
				id: "view",
			},
			{
				title: "Delete",
				id: "delete",
			},
		];
	}

	let gtc = gridTemplateColumns;

	let unorderedCorrection = 0;

	const appendCells = [];
	if (onMenuClick) {
		appendCells.push({ value: "__actions", header: "__actions" });
		unorderedCorrection++;
	}

	if (onExpand) {
		appendCells.unshift({
			value: "__expandBtn",
			header: "__expandBtn",
		});
		unorderedCorrection++;
	}

	if (bulk) {
		appendCells.unshift({ header: "__bulk", value: "__bulk" });
		unorderedCorrection++;
	}

	if (itemType) {
		appendCells.unshift({ header: "__itemType", value: itemType });
		unorderedCorrection++;
	}

	if (onExpand) {
		appendCells.push({ value: "__expandable", header: "__expandable" });
		unorderedCorrection++;
	}

	if (internal) {
		appendCells.unshift({ value: "__treelines", header: "__treelines" });
		unorderedCorrection++;
	}

	let ord = null;
	let correction = 0;
	if (order) {
		ord = [...order];
		if (!onMenuClick) {
			ord = ord.filter(item => item !== "__actions");
		} else if (!ord.includes("__actions" as any)) {
			ord.push("__actions" as any);
		} else correction++;

		if (appendCells.find(val => val.header === "__itemType")) {
			if (!ord.includes("__itemType" as any)) ord.unshift("__itemType" as any);
			else correction++;
		}

		if (appendCells.find(val => val.header === "__expandBtn")) {
			if (!ord.includes("__expandBtn" as any)) ord.unshift("__expandBtn" as any);
			else correction++;
		}

		if (appendCells.find(val => val.header === "__bulk")) {
			if (!ord.includes("__bulk" as any)) ord.unshift("__bulk" as any);
			else correction++;
		}

		if (appendCells.find(val => val.header === "__expandable")) {
			if (!ord.includes("__expandable" as any)) ord.push("__expandable" as any);
			else correction++;
		}

		if (internal && !ord.includes("__treelines" as any)) ord.unshift("__treelines" as any);
		else correction++;
	}

	let itemStatusDivider = false;

	let dividerIndex = 0; // bulk ? 1 : 0;

	if (ord && ord[dividerIndex] && ord[dividerIndex] === "__itemType" && ord[dividerIndex + 1] && ord[dividerIndex + 1] === "status") {
		appendCells.push({
			value: "__itemOrderDivider",
			header: "__itemOrderDivider",
		});

		ord.splice(1, 0, "__itemOrderDivider" as any);
		itemStatusDivider = true;
	}

	if (!gtc) {
		gtc = "";
		let passed = 0;

		let keysCount = 0;
		if (ord) {
			keysCount = ord.length;
		} else {
			if (data && data[0] && typeof data[0] === "object") {
				keysCount = Object.keys(data[0]).length + unorderedCorrection;
			} else {
				keysCount = unorderedCorrection + 1;
			}
		}

		// treelines
		if (internal) {
			gtc += "max-content";
			passed++;
		}

		// add item
		if (itemType) {
			gtc += " max-content";
			passed++;
		}

		// add status
		if (data && data[0] && typeof data[0] === "object" && Object.keys(data[0]).find(val => val.toLowerCase().trim() === "status")) {
			gtc += " max-content";
			passed++;
		}

		// add item > order divider
		if (itemStatusDivider) {
			gtc += " max-content";
			passed++;
		}

		// add bulk
		if (bulk) {
			gtc += " max-content";
			passed++;
		}

		// add expand btn
		if (onExpand) {
			gtc += " max-content";
			passed++;
		}

		if (!inlineItems) {
			// add first big field
			gtc += " 1fr";
			passed++;
		} else {
			passed += 3;
		}

		// add all fields
		if (keysCount - passed > 0) {
			//gtc += ` repeat(${keysCount - passed},max-content)`;
			if (keysCount - passed - 1 > 0) gtc += ` repeat(${keysCount - passed - 1},minmax(50px,1fr))`;
			gtc += ` auto`;
		}

		if (inlineItems) gtc += " 1fr max-content max-content ";
	}

	let renderData = data;

	let filterBox = [];
	if (searchable) filterBox.push(<RowsSearch rowsID={rowsID} />);
	if (filters) filterBox.push(<RowsFilters key={rowsID} rowsID={rowsID} filters={filters} />);

	const table = (
		<RowTable
			ignoreClick={ignoreClick}
			data={renderData}
			ord={ord}
			gtc={gtc}
			onExpand={onExpand}
			internal={internal}
			onClick={onClick}
			appendCells={appendCells}
			cellFactory={cellFactory}
			rowsID={rowsID}
			menu={menu}
			width={width}
		/>
	);

	let content = table;
	if (searchable) {
		content = (
			<Box
				sx={{
					flexGrow: 1,
					position: "relative",
					overflow: "auto",
					"&>div": {
						// position: "absolute",
						width: "100%",
						height: "100%",
					},
				}}>
				<div>{table}</div>
			</Box>
		);
	}

	return (
		<VBox
			ref={ref}
			sx={{
				position: "relative",
				flexGrow: 1,
			}}>
			{filterBox.length > 0 && (
				<Tile sx={{ borderRadius: "0px", paddingRight: "0px", paddingLeft: "0px" }}>
					<VBox gap="15px" sx={{ paddingLeft: controlsPadding ?? "0px", paddingRight: controlsPadding ?? "0px" }}>
						{filterBox.map(val => (
							<Fragment key={val.key}>{val}</Fragment>
						))}
					</VBox>
				</Tile>
			)}
			{(searchable || filters) && <Spacer size="6px" />}

			{content}

			<RowMenus onMenuClick={onMenuClick} menu={menu} parentRef={ref} rowsID={rowsID} />
		</VBox>
	);
};

const RowsFilters = <T,>({
	rowsID,
	//tiled,
	filters,
}: {
	rowsID: string;
	tiled?: boolean;
	filters: IRowsFilters<T>[];
}) => {
	const [currenFilter, setCurrentFilter] = useState<IRowsFilters<T> | null>(null);
	useEffect(() => {
		S_MENU_FILTER.invoke({
			rowsID: rowsID,
			data: currenFilter?.mask ? currenFilter : null,
		});
	}, [currenFilter]);

	return (
		<VBox>
			<Typo variant="caption">Filter:</Typo>
			<Spacer size="5px" />
			<HBox gap={"10px"}>
				{filters.map((val: IRowsFilters<T>, index: number) => {
					return (
						<Box
							sx={{
								color: currenFilter === val ? "#FFFFFF" : undefined,
								border: "1px solid rgba(0,0,0,.1)",
								padding: "5px 15px",
								fontSize: "13px",
								borderRadius: "20px",
								cursor: currenFilter !== val ? "pointer" : undefined,
								backgroundColor: currenFilter === val ? "#353745" : undefined,
							}}
							key={index}
							onClick={() => {
								setCurrentFilter(val);
							}}>
							{val.name}
						</Box>
					);
				})}
			</HBox>
		</VBox>
	);
};

const RowsSearch = ({ rowsID /*tile */ }: any) => {
	const [search, setSearch] = useState<string>("");

	useEffect(() => {
		const tm = setTimeout(() => {
			S_SEARCH.invoke({
				rowsID: rowsID,
				data: search,
			});
		}, 500);
		return () => {
			clearTimeout(tm);
		};
	}, [search]);

	return (
		<Input
			size="medium"
			type="search"
			label="Search"
			name="search"
			onChange={val => {
				setSearch(val);
			}}
			data={{ search: search ?? "" }}
		/>
	);
};

const RowMenus = ({ onMenuClick, parentRef, menu, rowsID }: any) => {
	//const data = S_MENU_SHOW.use([]);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const [data, setData] = useState<{
		rowsID: string;
		data: any;
	}>();

	useEffect(() => {
		const s = S_MENU_SHOW.subscribe(req => {
			setData(req);
		});
		return () => {
			S_MENU_SHOW.unsubscribe(s);
		};
	}, []);

	useEffect(() => {
		if (!data) return;
		if (data.rowsID !== rowsID) return; // another rows component, ignore

		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") S_MENU_SHOW.invoke({ rowsID, data: null });
		};

		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				S_MENU_SHOW.invoke({ rowsID, data: null });
			}
		};

		if (data.data) {
			window.addEventListener("keyup", handleKey);
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			window.removeEventListener("keyup", handleKey);
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			window.removeEventListener("keyup", handleKey);
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [data, rowsID, parentRef]);

	if (!data || data.rowsID !== rowsID || data.data === null) return null;

	const { rect, row, rowID } = data.data;
	const menuItems = resolveRowMenuItems(menu, row, rowID);
	if (menuItems.length === 0) return null;

	return (
		<VBox
			ref={menuRef}
			sx={{
				position: "absolute",
				top: rect.top - (parentRef?.current?.getBoundingClientRect().top ?? 0) + "px",
				right: "20px",
				zIndex: 1,
				padding: "15px 20px",
				backgroundColor: "#555767",
				borderRadius: "6px",
				color: "buttonTextPrimary",
				gap: "10px",
				cursor: "pointer",
				boxShadow: "0 0 20px rgba(0,0,0,.1)",
			}}>
			{menuItems.map((item, i: number) => {
				return (
					<TypoMedium
						key={i}
						onClick={() => {
							onMenuClick?.(item.id, row, rowID);
							S_MENU_SHOW.invoke({ rowsID, data: null });
						}}>
						{item.title}
					</TypoMedium>
				);
			})}
		</VBox>
	);
};

const RowTable = <T,>({ ignoreClick, ord, gtc, onExpand, internal, onClick, appendCells, data, cellFactory, rowsID, menu }: any) => {
	//const search = S_SEARCH.use([]);
	const filter = S_MENU_FILTER.use([]);
	let preparedData = data;
	let performSearch = false;

	const [search, setSearch] = useState<{
		rowsID: string;
		data: any;
	}>();

	useEffect(() => {
		const handler = S_SEARCH.subscribe(data => {
			setSearch(data);
		});
		return () => {
			S_SEARCH.unsubscribe(handler);
		};
	}, []);

	if (search?.rowsID == rowsID && search?.data && typeof search.data === "string" && search.data.length > 1) {
		performSearch = true;
		const sv = search.data.toLowerCase().split(",");
		preparedData = preparedData.filter((val: T) => {
			for (let i in val) {
				for (let j in sv) {
					if (!sv[j]) continue;
					const mask = sv[j].toLowerCase().trim().split(" "); // each search value separated by comma
					const compare = (val[i] + "").toLowerCase().trim().split(" "); // each field value splitted by space

					let found = 0;
					let total = mask.length;
					for (let maskValue of mask) {
						maskValue = maskValue.replaceAll(/[\'\"\-\.]/g, "");

						for (let fieldValue of compare) {
							fieldValue = fieldValue.replaceAll(/[\'\"\-\.]/g, "");
							const index = fieldValue.indexOf(maskValue);
							if (index === 0) found++;
						}
					}
					if (found > total * 0.8) return true;
				}
			}
			return false;
		});
	}

	if (filter && filter.rowsID === rowsID && filter.data !== null && !performSearch) {
		preparedData = preparedData.filter((val: any) => {
			return val[filter.data!.field] === filter.data?.mask;
		});
	}

	if (preparedData.length === 0) return null;

	//const tm = React.memo(<div></div>,(pp,ep)=>{return true})
	const table = (
		<Table<T>
			sx={{
				rowGap: "4px",
			}}
			ignoreClick={ignoreClick}
			headless
			data={preparedData}
			order={(ord as any) ?? undefined}
			gridTemplateColumns={gtc}
			onRowClick={(row, cell, rowID) => {
				if (cell === "__bulk" || cell === "__actions") return;

				if (onExpand && cell === "__expandBtn") {
					S_TABLE_EXPAND.invoke({
						rowID: rowID,
						content: onExpand(row, rowID),
						isParentInternal: internal ?? false,
					});
					return;
				}

				if (onClick) onClick(row, cell);
			}}
			appendCells={appendCells}
			cellFactory={(row, cell, index: number, key, name, expanded, rowID) => {
				let radius = "";

				if (index === (internal ? 1 : 0)) radius = "6px 0 0 6px";

				const rowLen = (ord?.length ?? Object.keys(preparedData[0] ?? {}).length) - 1;
				if (
					// Last cell
					(rowLen === index && name !== "__expandable") ||
					// OR cell before last if the last one is expandable (fake)
					(rowLen - 1 === index && ord?.at(-1) === "__expandable")
				)
					radius = "0 6px 6px 0";

				const sx = {
					borderRadius: radius,
					padding: name !== "__expandable" ? "10px" : undefined,
					//	border: "1px solid red",
				};

				if (cell !== "__actions" && name !== "__expandable" && cell !== "__itemOrderDivider") sx.padding = "10px 40px 10px 20px";

				if (index === 0) sx.padding = "10px 0px 10px 20px";

				if (cell === "__actions") sx.padding = "10px 5px";
				if (name === "__bulk" || name === "__expandBtn") sx.padding = "0px 0px 0px 20px";

				if (cellFactory) {
					const cfr = cellFactory(row, cell, index, key, name, sx, expanded, rowID);

					if (cfr && cfr.sx && cfr.content !== cell) {
						return cfr;
					}
					if (cfr?.content && typeof cfr?.content === "object") return cfr;

					if (cfr?.content) cell = cfr.content;
				}

				if (name === "__bulk") {
					return {
						content: <Checkbox />,
						sx: sx,
					};
				}

				if (name === "__expandBtn") {
					return {
						content: (
							<Box>
								<IconArrowDown
									scheme={!expanded ? "light" : "dark"}
									sx={{
										transition: "transform 0.2s",
										transform: expanded ? "rotate(180deg)" : "rotate(0)",
									}}
								/>
							</Box>
						),
						sx: sx,
					};
				}

				if (name === "__itemOrderDivider") {
					return {
						content: <IconArrowRight width="5px" scheme={!expanded ? "light" : "dark"} />,
						sx: { padding: "0px" },
					};
				}
				if (name === "status") {
					return {
						content: <Badge key={key} label={cell} />,
						sx: {
							...sx,
							padding: "10px",
						},
					};
				}

				if (name === "__itemType") {
					return {
						content: <Badge key={key} label={cell} />,
						sx: {
							...sx,
							padding: "10px 10px 10px 20px",
						},
					};
				}

				if (name === "__actions" && cell === "__actions") {
					if (resolveRowMenuItems(menu, row, rowID ?? "").length === 0) {
						return {
							content: null,
							sx: { ...sx, borderRadius: "0 6px 6px 0" },
						};
					}

					return {
						content: (
							<Button
								variant="icon"
								sx={{ aspectRatio: "unset !important" }}
								onClick={e => {
									S_MENU_SHOW.invoke({
										rowsID: rowsID,
										data: null,
									});

									S_MENU_SHOW.invoke({
										rowsID: rowsID,
										data: {
											rect: e.target.getBoundingClientRect(),
											row: row,
											rowID: rowID ?? "",
										},
									});
								}}
								icon={<IconVDots scheme={expanded ? "dark" : "light"} />}
							/>
						),
						sx: {
							...sx,
							borderRadius: "0 6px 6px 0",
						},
					};
				}

				return {
					content: <TitledValue title={name ?? "--"} value={cell ?? "--"} />,
					sx: {
						...sx,
						//padding: "10px 30px",
					},
				};
			}}
		/>
	);

	return table;
};
